import type { RuleGroup, Segment } from './types';

const CREATED = '2026-06-01T09:00:00Z';

function segment(id: string, name: string, description: string, rules: RuleGroup, prebuiltKey: string | null, createdAt = CREATED): Segment {
  return { id, name, description, type: prebuiltKey ? 'prebuilt' : 'custom', prebuiltKey, rules, createdAt, updatedAt: createdAt };
}

const and = (...children: RuleGroup['children']): RuleGroup => ({ op: 'and', children });

/**
 * Smart segments (CM-P0-7). Membership is worked out live from the rules.
 * SEG-01 to SEG-09 are prebuilt; their thresholds are defaults and editable per brand.
 */
export const mockSegments: Segment[] = [
  segment('seg_new', 'New customers', 'One order, placed in the last 30 days', and(
    { field: 'order_count', operator: 'eq', value: 1 },
    { field: 'first_order_days_ago', operator: 'lt', value: 30 },
  ), 'SEG-01'),
  segment('seg_regulars', 'Regulars', '3 or more orders in the last 60 days', and(
    { field: 'order_count', operator: 'gte', value: 3, periodDays: 60 },
  ), 'SEG-02'),
  segment('seg_at_risk', 'At risk', '2 or more orders, last one 45 to 90 days ago', and(
    { field: 'order_count', operator: 'gte', value: 2 },
    { field: 'last_order_days_ago', operator: 'between', value: [45, 90] },
  ), 'SEG-03'),
  segment('seg_lapsed', 'Lapsed', 'No order for more than 90 days', and(
    { field: 'last_order_days_ago', operator: 'gt', value: 90 },
  ), 'SEG-04'),
  segment('seg_high_spenders', 'High spenders', 'Top 10% by spend in the last 180 days', and(
    { field: 'spend_top_percent', operator: 'lte', value: 10, periodDays: 180 },
  ), 'SEG-05'),
  segment('seg_members', 'Loyalty members', 'Customers with an active loyalty membership', and(
    { field: 'loyalty_member', operator: 'eq', value: true },
  ), 'SEG-06'),
  segment('seg_non_members', 'Non-members', 'Linked customers who have not joined loyalty', and(
    { field: 'loyalty_member', operator: 'eq', value: false },
  ), 'SEG-07'),
  segment('seg_birthday', 'Birthday this month', 'Customers whose birthday falls in the current month', and(
    { field: 'birthday_this_month', operator: 'eq', value: true },
  ), 'SEG-08'),
  segment('seg_reachable', 'All reachable customers', 'Anyone with marketing consent on at least one channel', and(
    { field: 'reachable', operator: 'eq', value: true },
  ), 'SEG-09'),
  segment('seg_not_on_app', 'Not on the app yet', 'Customers who have not installed the Customer App', and(
    { field: 'has_app', operator: 'eq', value: false },
  ), 'APP-01'),
  segment('seg_app_customers', 'App customers', 'Customers who use the Customer App', and(
    { field: 'has_app', operator: 'eq', value: true },
  ), 'APP-02'),
  segment('seg_dosa_fans', 'Dosa fans', 'Ordered a dosa in the last 90 days', and(
    { field: 'ordered_category_id', operator: 'in', value: ['cat_dosa'], periodDays: 90 },
  ), null, '2026-08-14T16:20:00Z'),
  segment('seg_lunch_members', 'Lunch-time members', 'Loyalty members who order at lunch', and(
    { field: 'loyalty_member', operator: 'eq', value: true },
    { field: 'daypart', operator: 'in', value: ['lunch'] },
  ), null, '2026-08-28T11:05:00Z'),
  segment('seg_big_tickets', 'Big-ticket or frequent', 'High average order value, or 5+ orders in 90 days', and(
    { field: 'last_order_days_ago', operator: 'lt', value: 120 },
    { op: 'or', children: [
      { field: 'avg_order_value_cents', operator: 'gte', value: 4500 },
      { field: 'order_count', operator: 'gte', value: 5, periodDays: 90 },
    ] },
  ), null, '2026-09-10T08:45:00Z'),
  segment('seg_vip_group', 'VIP group', 'Everyone in the VIPs customer group', and(
    { field: 'group_id', operator: 'in', value: ['grp-vip'] },
  ), null, '2026-09-18T10:30:00Z'),
];
